import { ImagePaths } from '../../lib/imageUrls.js';

export const ms102Microsoft365Administrator = {
  id: 54,
  slug: 'ms-102-microsoft-365-administrator',
  ogImage: '/og/courses/ms-102-microsoft-365-administrator.jpg',
  courseCode: 'MS-102-001',
  featured: false,
  title: 'MS-102 Microsoft 365 Administrator',
  subtitle: 'Manage Microsoft 365 tenants, identity, security, and compliance at enterprise scale',

  valueProposition: 'MS-102 is the successor to the retired MS-100 and MS-101 exams — the role-based certification for Microsoft 365 tenant administrators. This programme prepares administrators to manage Microsoft 365 services, configure identity with Microsoft Entra ID, implement security and compliance controls, and support the Microsoft 365 application lifecycle — all mapped to Australian regulatory requirements and ASD Essential Eight controls.',

  duration: '40 hours (5 days intensive or 10 weeks part-time)',
  students: 'New',
  level: 'Intermediate',
  organization: 'Microsoft',
  image: ImagePaths.COURSE_1,
  shortDescription: 'MS-102-001 — Microsoft 365 Administrator certification preparation covering tenant management, Entra ID, security, and compliance for Australian M365 admins.',

  price: {
    aud: 1599,
    currency: 'AUD',
    earlyBird: 1299
  },

  stripeConfig: {
    priceId: 'price_1U3WMpKu1nyc322UBQIdu0x6',
    productId: 'prod_V3deG1PUy8INcW'
  },

  snapshot: {
    whoItsFor: [
      'Microsoft 365 global administrators managing Australian organisational tenants',
      'IT managers previously certified on the retired MS-100 and MS-101 exams seeking recertification',
      'System administrators transitioning from on-premises Exchange and Active Directory to Microsoft 365',
      'Helpdesk leads stepping up to tenant-level Microsoft 365 administration responsibilities'
    ],
    accreditation: 'Preparation for Microsoft Certified: Microsoft 365 Administrator Expert (Exam MS-102). Prerequisite: Microsoft 365 Fundamentals (MS-900) or equivalent experience recommended.'
  },

  logistics: {
    classSize: 'Maximum 14 participants — self-paced with weekly live sessions and lab reviews',
    requirements: [
      'Working knowledge of Microsoft 365 services: Exchange Online, SharePoint Online, Teams',
      'Basic familiarity with Microsoft Entra ID (Azure AD) and Microsoft 365 Admin Centre',
      'Microsoft 365 global administrator access for lab exercises (trial tenant acceptable)',
      'PowerShell experience beneficial for automation and scripting modules'
    ]
  },

  overview: 'The MS-102 Microsoft 365 Administrator programme covers every domain of the current MS-102 exam blueprint — deploying and managing a Microsoft 365 tenant, implementing and managing identity in Microsoft Entra ID, managing security and compliance, and managing the Microsoft 365 workload applications. MS-102 replaces the retired MS-100 (Microsoft 365 Identity and Services) and MS-101 (Microsoft 365 Mobility and Security) exams, which Microsoft retired on 30 September 2023. This programme explicitly bridges the retired exam content to the current MS-102 blueprint, making it suitable for Australian administrators who hold or previously held the older certifications and need to update their credentials. In the Australian context, tenant configuration decisions are mapped to the ASD Essential Eight, the ACSC Information Security Manual tenant hardening guidance, and Privacy Act 1988 data residency and handling obligations.',

  outcomes: [
    'Configure and manage a Microsoft 365 tenant including licences, subscriptions, and service health monitoring',
    'Implement and manage Microsoft Entra ID: user provisioning, group management, and hybrid identity with Connect Sync',
    'Configure and manage authentication: MFA, passwordless, self-service password reset, and Conditional Access policies',
    'Implement Microsoft 365 security: Secure Score, Defender for Business, and threat protection for SMB and enterprise',
    'Configure Microsoft Purview compliance: data governance, sensitivity labels, DLP basics, and retention policies',
    'Manage Microsoft 365 applications: Exchange Online mail flow, SharePoint sites, Teams policies, and OneDrive governance',
    'Monitor Microsoft 365 service health, run usage reports, and manage Microsoft 365 Apps update channels',
    'Pass the Microsoft MS-102 Microsoft 365 Administrator Expert exam'
  ],

  audience: [
    'Microsoft 365 global administrators running tenants for Australian organisations',
    'IT managers previously certified on the retired MS-100 and MS-101 exams seeking a current credential',
    'System administrators migrating from on-premises Exchange and Active Directory to Microsoft 365',
    'Service desk and helpdesk leads stepping up to tenant-level administration',
    'Managed service provider engineers administering multiple client Microsoft 365 tenants',
    'IT staff in local government, education and professional services responsible for M365 security posture'
  ],

  prerequisites: [
    'Working knowledge of Microsoft 365 services: Exchange Online, SharePoint Online and Teams',
    'Basic familiarity with Microsoft Entra ID and the Microsoft 365 Admin Centre',
    'Microsoft 365 Fundamentals (MS-900) or equivalent practical experience',
    'Global Administrator access to a Microsoft 365 tenant for lab exercises (trial tenant acceptable)',
    'PowerShell experience is beneficial for the automation and bulk operations modules'
  ],

  modules: [
    {
      number: 1,
      title: 'Deploy and Manage a Microsoft 365 Tenant',
      duration: '8 hours',
      topics: [
        'Microsoft 365 tenant architecture: domains, subscriptions, licences, and administrative roles',
        'Manage Microsoft 365 via Admin Centre, PowerShell (Microsoft Graph PowerShell SDK), and Graph API',
        'Configure Microsoft 365 Apps for Enterprise: deployment channels, update policies, and Office customisation',
        'Monitor service health, configure communication preferences, and manage support escalations',
        'Microsoft 365 data residency for Australian organisations: Multi-Geo, data location commitments, and OAIC obligations'
      ]
    },
    {
      number: 2,
      title: 'Implement and Manage Microsoft Entra ID',
      duration: '8 hours',
      topics: [
        'Create and manage users, groups, and administrative units at scale using Microsoft Graph and bulk operations',
        'Configure Microsoft Entra Connect Sync and Cloud Sync for hybrid identity',
        'Implement external identities: B2B collaboration, cross-tenant access settings, and guest user lifecycle',
        'Manage licences: group-based licensing, licence conflict resolution, and PowerShell automation',
        'Entra ID audit logs and sign-in logs: configuration, export to Log Analytics, and Notifiable Data Breach detection'
      ]
    },
    {
      number: 3,
      title: 'Implement and Manage Identity and Access',
      duration: '8 hours',
      topics: [
        'Configure multi-factor authentication and passwordless authentication aligned to ASD Essential Eight MFA requirements',
        'Conditional Access: Named Locations, device compliance, sign-in frequency, and persistent browser sessions',
        'Privileged Identity Management (PIM): just-in-time global admin activation and access reviews',
        'Microsoft Entra ID Protection: risk policies, risky users, and sign-in risk remediation',
        'Self-service password reset, smart lockout, and password protection for on-premises and cloud'
      ]
    },
    {
      number: 4,
      title: 'Manage Security and Threats in Microsoft 365',
      duration: '8 hours',
      topics: [
        'Microsoft Secure Score: reading recommendations, implementing controls, and tracking Essential Eight posture',
        'Defender for Office 365: anti-phishing, anti-malware, safe links, safe attachments, and Attack Simulator',
        'Defender for Business: simplified endpoint protection for SMB Microsoft 365 Business Premium tenants',
        'Microsoft Purview compliance basics: Content search, audit logs, retention policies, and eDiscovery fundamentals',
        'Sensitivity labels and DLP essentials for Privacy Act 1988 compliance'
      ]
    },
    {
      number: 5,
      title: 'Manage Microsoft 365 Workload Applications',
      duration: '8 hours',
      topics: [
        'Exchange Online: mail flow rules, connectors, accepted domains, and anti-spam configuration',
        'SharePoint Online and OneDrive: site creation governance, external sharing settings, and storage policies',
        'Microsoft Teams: meeting policies, messaging policies, and Phone System for Australian organisations',
        'Microsoft 365 Groups and governance: naming policies, expiration, and lifecycle management',
        'MS-102 exam preparation: domain-by-domain review, practice questions, and mock exam walkthrough'
      ]
    }
  ],

  format: {
    type: 'Self-paced online modules with weekly live sessions — 5 days intensive or 10 weeks part-time',
    includes: [
      '40 hours of instruction across all five MS-102 exam domains',
      'Weekly live sessions with lab reviews and administrator Q&A',
      'Tenant configuration labs covering identity, security, compliance and workload administration',
      'Microsoft Graph PowerShell scripts for bulk licensing, provisioning and reporting',
      'MS-100/MS-101 to MS-102 bridging guide for previously certified administrators',
      'Timed MS-102 mock exam with domain-by-domain review',
      'Recorded sessions and lab guides for 12 months',
      'Certificate of completion and 30 days of post-course email support'
    ]
  },

  nextIntake: {
    date: 'Register your interest — we will confirm the next cohort',
    details: 'Five-day intensive cohorts run every second month; the 10-week part-time stream starts monthly with one live session per week. Private cohorts can align labs to your own tenant configuration'
  },

  certification: 'Prepares participants for Microsoft Certified: Microsoft 365 Administrator Expert via Exam MS-102. Exam voucher purchased separately. Bridges content from the retired MS-100 and MS-101 exams.',

  faqs: [
    {
      question: 'I am certified on MS-100 and MS-101. Do I need to pass MS-102 to stay current?',
      answer: 'Yes. Microsoft retired MS-100 and MS-101 on 30 September 2023. If your certification renewal date has lapsed, you will need to pass MS-102 to re-earn the Microsoft 365 Administrator Expert credential. This course is specifically structured to bridge the MS-100/MS-101 content to the current MS-102 exam blueprint.'
    },
    {
      question: 'Is the course suitable for small business Microsoft 365 admins?',
      answer: 'Yes, with context. Modules 1-3 are directly applicable to administrators of any size Microsoft 365 tenant. Module 4 covers both Defender for Business (for Microsoft 365 Business Premium tenants) and Defender for Office 365 (for E3/E5 tenants), so SMB and enterprise participants both find relevant content. Module 5 is more enterprise-weighted.'
    },
    {
      question: 'Does the course cover Microsoft Copilot administration?',
      answer: 'Yes. Module 1 and Module 4 cover Microsoft 365 Copilot licence management, Copilot readiness settings in the Admin Centre, and the sensitivity label and DLP controls required before enabling Copilot for end users — aligned to the Copilot governance guidance in the ACSC Essential Eight controls.'
    },
    {
      question: 'Can this course be delivered to our entire IT operations team?',
      answer: 'Yes. We regularly run MS-102 programmes for Microsoft 365 administration teams in local government, education, and professional services. Private cohorts can align lab exercises to your specific tenant configuration and organisational policies.'
    }
  ],

  australianContext: true,
  complianceFrameworks: ['ASD Essential Eight', 'ACSC ISM', 'Privacy Act 1988', 'OAIC APPs']
};
